import { useEffect, useRef, useCallback } from 'react';

/**
 * useVAD Hook (Voice Activity Detection)
 *
 * Detects when user starts/stops speaking using audio energy analysis
 * No wake word, no button - fully automatic
 *
 * @param {AnalyserNode} analyser - Web Audio API analyser node
 * @param {Function} onSpeechStart - Callback when speech detected
 * @param {Function} onSpeechEnd - Callback when silence detected
 * @param {Object} options - Configuration options
 */
export const useVAD = (analyser, onSpeechStart, onSpeechEnd, options = {}) => {
  const {
    energyThreshold = 0.02,      // Minimum energy to consider as speech
    silenceDuration = 800,        // ms of silence before calling onSpeechEnd
    smoothingFactor = 0.95,       // Smoothing for energy calculation
    enabled = true,               // Can disable VAD
    minSpeechDuration = 150       // Minimum ms of continuous speech before triggering
  } = options;

  const isSpeakingRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const rafIdRef = useRef(null);
  const smoothedEnergyRef = useRef(0);
  const speechStartTimeRef = useRef(null);  // Track when speech started
  const speechTriggeredRef = useRef(false);  // Track if we've triggered onSpeechStart

  /**
   * Calculate audio energy from frequency data
   */
  const calculateEnergy = useCallback((dataArray) => {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const value = dataArray[i] / 255; // Normalize to 0-1
      sum += value * value;
    }
    return Math.sqrt(sum / dataArray.length);
  }, []);

  /**
   * Main VAD loop - runs continuously
   */
  const vadLoop = useCallback(() => {
    if (!analyser || !enabled) {
      rafIdRef.current = requestAnimationFrame(vadLoop);
      return;
    }

    // Get frequency data
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    // Calculate current energy
    const energy = calculateEnergy(dataArray);

    // Apply exponential smoothing
    smoothedEnergyRef.current =
      smoothingFactor * smoothedEnergyRef.current +
      (1 - smoothingFactor) * energy;

    const currentEnergy = smoothedEnergyRef.current;

    // Speech detected (energy above threshold)
    if (currentEnergy > energyThreshold) {
      // Clear silence timer if exists
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      // Start tracking speech duration if not already
      if (!speechStartTimeRef.current) {
        speechStartTimeRef.current = Date.now();
        speechTriggeredRef.current = false;
      }

      // Check if we've met minimum speech duration
      const speechDuration = Date.now() - speechStartTimeRef.current;
      if (speechDuration >= minSpeechDuration && !speechTriggeredRef.current) {
        isSpeakingRef.current = true;
        speechTriggeredRef.current = true;
        console.log('🗣️  Speech START detected (energy:', currentEnergy.toFixed(4), 'duration:', speechDuration, 'ms)');
        onSpeechStart?.();
      }
    }
    // Silence detected (energy below threshold)
    else {
      // Reset speech start tracking if we haven't triggered yet (noise burst)
      if (speechStartTimeRef.current && !speechTriggeredRef.current) {
        console.log('⚠️  Ignoring noise burst (duration:', Date.now() - speechStartTimeRef.current, 'ms < min:', minSpeechDuration, 'ms)');
        speechStartTimeRef.current = null;
      }

      // If we're currently speaking, start silence timer
      if (isSpeakingRef.current && currentEnergy <= energyThreshold) {
        if (!silenceTimerRef.current) {
          silenceTimerRef.current = setTimeout(() => {
            if (isSpeakingRef.current) {
              isSpeakingRef.current = false;
              speechStartTimeRef.current = null;
              speechTriggeredRef.current = false;
              console.log('🔇 Speech END detected (silence duration:', silenceDuration, 'ms)');
              onSpeechEnd?.();
            }
          }, silenceDuration);
        }
      }
    }

    rafIdRef.current = requestAnimationFrame(vadLoop);
  }, [analyser, enabled, calculateEnergy, energyThreshold, silenceDuration, smoothingFactor, minSpeechDuration, onSpeechStart, onSpeechEnd]);

  // Start VAD loop
  useEffect(() => {
    if (analyser && enabled) {
      vadLoop();
    }

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, [analyser, enabled, vadLoop]);

  return {
    isSpeaking: isSpeakingRef.current,
    currentEnergy: smoothedEnergyRef.current
  };
};
